import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    FaGamepad,
    FaUsers,
    FaShoppingBag,
    FaReceipt,
    FaHeart,
    FaCrown,
    FaGift,
    FaBoxOpen,
    FaCreditCard,
    FaCog,
    FaChevronLeft,
    FaChevronRight,
    FaUtensils,
    FaShieldAlt,
    FaFire,
    FaStar,
    FaTicketAlt,
    FaTwitter,
    FaInstagram,
    FaFacebook,
    FaDiscord,
} from "react-icons/fa";
import { BsBackpack4 } from "react-icons/bs";
import { IStore } from "@/redux/defaultStore";
import useThirwebWallet from "@/hooks/useThirwebWallet";
import { UtilsApi, VersionResponse } from "@devour/client";
import getConfig from "@/utils/getConfig";
import FrameButton from "@/components/buttons/FrameButton";

interface SidebarMenuItem {
    id: string;
    label: string;
    icon: React.ComponentType<any>;
    path: string;
    badge?: number;
    comingSoon?: boolean;
}

const SIDEBAR_MENU_ITEMS: SidebarMenuItem[] = [
    // Gaming Section
    {
        id: "play",
        label: "Play",
        icon: FaGamepad,
        path: "/play",
    },
    {
        id: "guilds",
        label: "Guilds",
        icon: FaShieldAlt,
        path: "/guilds",
    },
    {
        id: "govip",
        label: "GoVIP",
        icon: FaCrown,
        path: "/go-vip-dashboard",
    },
    // Commerce Section
    {
        id: "lootboxes",
        label: "LootBoxes",
        icon: FaBoxOpen,
        path: "/devourlootbox",
    },
    {
        id: "loot-shop",
        label: "LootShop",
        icon: FaShoppingBag,
        path: "/lootshop",
    },
    {
        id: "my-orders",
        label: "Orders",
        icon: FaReceipt,
        path: "/menu-orders",
    },
    // Utility Section
    {
        id: "my-stash",
        label: "My Stash",
        icon: BsBackpack4,
        path: "/collectibles",
    },
    {
        id: "load-fuel",
        label: "Load Fuel",
        icon: FaFire,
        path: "/load-fuel",
    },
    // Discord community link
    {
        id: "discord",
        label: "Discord",
        icon: FaDiscord,
        path: "https://discord.gg/devourplay",
        isExternal: true,
    },
];

interface ExtendedSidebarMenuItem extends SidebarMenuItem {
    isExternal?: boolean;
}

const SIDEBAR_STORAGE_KEY = "devourplay-sidebar-collapsed";

const DevourPlaySidebar = React.memo(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const { onOpenThirdwebWallet } = useThirwebWallet();
    const [version, setVersion] = useState<VersionResponse>();

    /*
     * Load initial collapsed state based on screen width
     * 768-1259px: default to collapsed
     * 1260px+: default to open (or use stored preference)
     */
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const screenWidth = window.innerWidth;
        if (screenWidth >= 768 && screenWidth < 1260) {
            return true; // Default to collapsed on tablets/small screens
        }
        const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        return stored ? JSON.parse(stored) : false;
    });

    // Persist collapsed state to localStorage and update body class
    useEffect(() => {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(isCollapsed));

        // Update body class for CSS styling
        if (isCollapsed) {
            document.body.classList.add("devourplay-sidebar-collapsed");
            document.body.classList.remove("devourplay-sidebar-expanded");
        } else {
            document.body.classList.add("devourplay-sidebar-expanded");
            document.body.classList.remove("devourplay-sidebar-collapsed");
        }
    }, [isCollapsed]);

    // Set initial body class on mount
    useEffect(() => {
        if (isCollapsed) {
            document.body.classList.add("devourplay-sidebar-collapsed");
        } else {
            document.body.classList.add("devourplay-sidebar-expanded");
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove("devourplay-sidebar-collapsed", "devourplay-sidebar-expanded");
        };
    }, []);

    // Fetch version info - only when token changes or on mount
    useEffect(() => {
        if (fullToken) {
            new UtilsApi(getConfig(fullToken)).version()
                .then(setVersion)
                .catch(console.error);
        }
    }, [fullToken?.id]); // Only re-fetch if token ID changes

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleMenuClick = (item: ExtendedSidebarMenuItem) => {
        if (item.comingSoon) return;

        // Special handling for wallet - open modal instead of navigating
        if (item.id === "devourgo-wallet") {
            onOpenThirdwebWallet();
            return;
        }

        // Handle external links
        if (item.isExternal) {
            window.open(item.path, "_blank", "noopener,noreferrer");
            return;
        }

        navigate(item.path);
    };

    const isActiveRoute = (path: string) => {
    // Special case for LootShop - check both /lootshop and the actual merchant URL
        if (path === "/lootshop") {
            return location.pathname === "/lootshop" ||
             location.pathname === "/restaurants/674a30b9b53b4c9f4aee6587";
        }

        // Special case for Eat (/restaurants) - don't match if it's the LootShop
        if (path === "/restaurants") {
            // Don't highlight Eat if we're on the LootShop page
            if (location.pathname === "/restaurants/674a30b9b53b4c9f4aee6587" ||
          location.pathname === "/lootshop") {
                return false;
            }
            // Otherwise, check normally
            return location.pathname === path || location.pathname.startsWith(`${path}/`);
        }

        // For other routes, check exact match or if it starts with the path
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <aside className={`devourplay-sidebar ${isCollapsed ? "devourplay-sidebar_collapsed" : ""}`}>
            {/* Sidebar Header */}
            <div className="devourplay-sidebar_header">
                <div className="devourplay-sidebar_logo">
                    {!isCollapsed
                        ? <img
                            src="/images/devourplay-logo2.png"
                            alt="DevourPlay"
                            className="devourplay-sidebar_logo-image"
                        />
                        : <img
                            src="/images/devourplay-icon.png"
                            alt="DevourPlay Icon"
                            className="devourplay-sidebar_icon-image"
                            onClick={toggleSidebar}
                            style={{ cursor: "pointer" }}
                        />
                    }
                </div>
                {!isCollapsed &&
          <button
              className="devourplay-sidebar_toggle"
              onClick={toggleSidebar}
              aria-label="Collapse sidebar"
          >
              <FaChevronLeft />
          </button>
                }
            </div>

            {/* Navigation Menu */}
            <nav className="devourplay-sidebar_nav">
                <ul className="devourplay-sidebar_menu">
                    {SIDEBAR_MENU_ITEMS.map((item, index) =>
                        <React.Fragment key={item.id}>
                            {/* Add dividers: before LootBoxes (3), Eat (6), Industry Pass (9) */}
                            {/* Don't show divider before Eat if user is not logged in */}
                            {(index === 3 ||
                index === 6 && fullToken ||
                index === 9) &&
                <li className="devourplay-sidebar_divider" aria-hidden="true" />
                            }
                            {/* Hide Discord menu item in expanded state */}
                            {/* Hide certain items when not logged in */}
                            {item.id === "discord" && !isCollapsed ||
               !fullToken && (item.id === "my-stash" || item.id === "eat" || item.id === "favorites" || item.id === "my-orders") ? null
                                : <li className="devourplay-sidebar_menu-item">
                                    <button
                                        className={`devourplay-sidebar_menu-link ${
                                            isActiveRoute(item.path) ? "devourplay-sidebar_menu-link_active" : ""
                                        } ${item.comingSoon ? "devourplay-sidebar_menu-link_disabled" : ""}`}
                                        onClick={() => handleMenuClick(item)}
                                        disabled={item.comingSoon}
                                        title={isCollapsed ? item.label : undefined}
                                    >
                                        <item.icon className="devourplay-sidebar_menu-icon" />
                                        {!isCollapsed &&
                      <>
                          <span className="devourplay-sidebar_menu-label">{item.label}</span>
                          {item.badge &&
                          <span className="devourplay-sidebar_menu-badge">{item.badge}</span>
                          }
                          {item.comingSoon &&
                          <span className="devourplay-sidebar_coming-soon">Soon</span>
                          }
                      </>
                                        }
                                    </button>
                                </li>
                            }
                        </React.Fragment>)}
                </ul>
            </nav>

            {/* Social links - only show in expanded state */}
            {!isCollapsed &&
        <div className="devourplay-sidebar_socials">
            <a
                href="https://twitter.com/GoDevour"
                className="devourplay-sidebar_social-link"
                target="_blank"
                rel="noopener noreferrer"
            >
                <FaTwitter />
            </a>
            <a
                href="https://www.instagram.com/go_devour/"
                className="devourplay-sidebar_social-link"
                target="_blank"
                rel="noopener noreferrer"
            >
                <FaInstagram />
            </a>
            <a
                href="https://www.facebook.com/GoDevour"
                className="devourplay-sidebar_social-link"
                target="_blank"
                rel="noopener noreferrer"
            >
                <FaFacebook />
            </a>
            <a
                href="https://discord.gg/devourplay"
                className="devourplay-sidebar_social-link"
                target="_blank"
                rel="noopener noreferrer"
            >
                <FaDiscord />
            </a>
        </div>
            }

            {/* Version info */}
            {version &&
        <div className="devourplay-sidebar_version">
            {version.version.includes("-production")
            // For production, show version without the "-production" suffix
                ? `Version: ${version.version.replace("-production", "")}`
                :
            // For other environments, show full version
                `Version: ${version.version}`
            }
        </div>
            }
        </aside>
    );
});

DevourPlaySidebar.displayName = "DevourPlaySidebar";

export default DevourPlaySidebar;